import { noForwardCustomProps } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import { REWARD_BACKGROUND_COLORS } from '@src/constants/battleDemo'
import {
  BattleDemoRewardGroup,
  BattleDemoRewardLite
} from '@src/types/battleDemo'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  margin: 10px 0px;
  overflow-y: auto;
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 600px;
    max-height: 400px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    max-height: 350px;
  }
`

const TitleContainer = styled(Box)`
  align-items: center;
  background-color: #e2e2e4;
  border-bottom: 1px solid #cfced0;
  display: flex;
  justify-content: center;
  min-height: 60px;
  width: 100%;
`

const Title = styled(Typography)`
  color: black;
  flex: 1;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 10px;
    font-weight: 400;
    line-height: 10px;
  }
`

const Row = styled(Box)`
  display: flex;
  width: 100%;
`

const Cell = styled(
  Box,
  noForwardCustomProps(['backgroundColor'])
)<RewardTipsCellProps>`
  align-items: center;
  display: flex;
  flex: 1;
  height: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    height: 80px;
    padding: 10px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    height: 45px;
    padding: 5px;
  }
  ${props =>
    props.backgroundColor
      ? `
      background-color: ${props.backgroundColor}; 
      color: #febe36;
      justify-content: flex-start;
      text-align: left;
    `
      : `
      background-color: white;
      border: 1px solid #edecec;
      color: black;
      justify-content: flex-end;
      text-align: right;
    `}
`

const Text = styled(Typography)`
  color: inherit;
  white-space: pre-wrap;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 10px;
    font-weight: 400;
    line-height: 10px;
  }
`

const BattleDemoRewardTips: FC<Props> = (props: Props) => {
  const { t } = useTranslation()

  const { rewardGroups } = props

  const sortedRewardGroups = useMemo(() => {
    const diff = (a: BattleDemoRewardGroup, b: BattleDemoRewardGroup) => {
      return a.rankMin - b.rankMin
    }

    return R.sort(diff, rewardGroups)
  }, [rewardGroups])

  const flattenRewards = useCallback(
    (group: BattleDemoRewardGroup, type: 'rewardsBasic' | 'rewardsBonus') => {
      const operate = R.pipe(
        R.propOr([], type),
        R.map((reward: BattleDemoRewardLite) => {
          const { name, value } = reward

          return value > 0 ? `x${value} ${name}` : ''
        }),
        R.join('\n')
      )

      return operate(group)
    },
    []
  )

  const hasBonus = R.isNotEmpty(
    R.pathOr([], [0, 'rewardsBonus'], sortedRewardGroups)
  )

  return (
    <Container>
      <TitleContainer>
        <Title>{t('battle_demo.leaderboard.tips.title.rank')}</Title>
        <Title>{t('battle_demo.leaderboard.tips.title.basic')}</Title>
        {hasBonus && (
          <Title>{t('battle_demo.leaderboard.tips.title.bonus')}</Title>
        )}
      </TitleContainer>
      {RA.mapIndexed((group: BattleDemoRewardGroup, index: number) => {
        return (
          <Row key={`${group.rankMin}-${group.rankMax}`}>
            <Cell
              backgroundColor={
                REWARD_BACKGROUND_COLORS[
                  index % REWARD_BACKGROUND_COLORS.length
                ]
              }
            >
              <Text>
                {group.rankMin === group.rankMax
                  ? t('battle_demo.leaderboard.tips.rank', {
                      rank: group.rankMin
                    })
                  : t('battle_demo.leaderboard.tips.rank_range', {
                      minRank: group.rankMin,
                      maxRank: group.rankMax
                    })}
              </Text>
            </Cell>
            <Cell>
              <Text>{flattenRewards(group, 'rewardsBasic')}</Text>
            </Cell>
            {hasBonus && (
              <Cell>
                <Text>{flattenRewards(group, 'rewardsBonus')}</Text>
              </Cell>
            )}
          </Row>
        )
      }, sortedRewardGroups)}
    </Container>
  )
}

type RewardTipsCellProps = {
  backgroundColor?: string
}

type Props = {
  rewardGroups: BattleDemoRewardGroup[]
}

export default BattleDemoRewardTips
